import { SCADA_CUSTOM_FONT_OPTIONS } from "./scadaFonts.generated";

export { SCADA_CUSTOM_FONT_OPTIONS } from "./scadaFonts.generated";

export const SCADA_BUILTIN_FONT_OPTIONS = Object.freeze([
  { value: "Arial", label: "Arial" },
  { value: "Roboto", label: "Roboto" },
  { value: "Tahoma", label: "Tahoma" },
  { value: "Verdana", label: "Verdana" },
  { value: "Times New Roman", label: "Times New Roman" },
  { value: "monospace", label: "monospace" },
]);

export const SCADA_FONT_OPTIONS = Object.freeze([
  ...SCADA_BUILTIN_FONT_OPTIONS,
  ...SCADA_CUSTOM_FONT_OPTIONS,
]);

const knownFontFamilies = new Set(SCADA_FONT_OPTIONS.map((option) => option.value));
const normalizedCustomFontFamilies = new Map(
  SCADA_CUSTOM_FONT_OPTIONS.flatMap((option) => (
    [option.value, option.label, ...(option.legacyValues || [])]
      .map((value) => [value, option.value])
  ))
);
const SCADA_FONT_STYLE_ELEMENT_ID = "scada-custom-font-faces";

const escapeCSSString = (value) => String(value)
  .replace(/\\/g, "\\\\")
  .replace(/"/g, '\\"')
  .replace(/[\r\n\f]/g, " ");

const installScadaFontFaces = () => {
  if (typeof document === "undefined") return;
  const stylesheet = SCADA_CUSTOM_FONT_OPTIONS.map((option) => `@font-face {
  font-family: "${escapeCSSString(option.value)}";
  src: url("${escapeCSSString(option.url)}") format("truetype");
  font-display: swap;
  font-style: normal;
  font-weight: 100 900;
}`).join("\n\n");
  const existingStyle = document.getElementById(SCADA_FONT_STYLE_ELEMENT_ID);
  if (existingStyle) {
    if (existingStyle.textContent !== stylesheet) existingStyle.textContent = stylesheet;
    return;
  }
  const style = document.createElement("style");
  style.id = SCADA_FONT_STYLE_ELEMENT_ID;
  style.dataset.scadaFonts = "custom";
  style.textContent = stylesheet;
  document.head.appendChild(style);
};

export const isKnownScadaFontFamily = (value) => knownFontFamilies.has(String(value || ""));
export const normalizeScadaFontFamily = (value) =>
  normalizedCustomFontFamilies.get(String(value || "")) || value;
export const normalizeScadaFontProperties = (properties) => {
  if (!properties || typeof properties !== "object" || Array.isArray(properties)) return properties;
  let normalized = properties;
  Object.keys(properties).forEach((propertyName) => {
    if (!/fontFamily$/i.test(propertyName)) return;
    const normalizedFamily = normalizeScadaFontFamily(properties[propertyName]);
    if (normalizedFamily === properties[propertyName]) return;
    if (normalized === properties) normalized = { ...properties };
    normalized[propertyName] = normalizedFamily;
  });
  return normalized;
};

let preloadPromise;
export const preloadScadaFonts = () => {
  if (preloadPromise) return preloadPromise;
  if (typeof document === "undefined" || SCADA_CUSTOM_FONT_OPTIONS.length === 0) {
    preloadPromise = Promise.resolve();
    return preloadPromise;
  }
  installScadaFontFaces();
  if (!document.fonts?.load) {
    preloadPromise = Promise.resolve();
    return preloadPromise;
  }
  const fontLoadPromise = Promise.allSettled(
    SCADA_CUSTOM_FONT_OPTIONS.map((option) =>
      document.fonts.load(`16px "${String(option.value).replace(/"/g, '\\"')}"`, "0123456789ABCabc")
    )
  ).then(() => undefined);
  const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 5000));
  preloadPromise = Promise.race([fontLoadPromise, timeoutPromise]);
  return preloadPromise;
};
